import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { getMeasurePercentages } from '../../../common/src/measure_percentages.ts';
import { Copyright, Hymn, isSongFileMissing, SongFileExtension } from '../../../common/src/model.ts';
import { isBound } from '../../../common/src/model_util.ts';
import {
  MeasureVisit,
  parseSypFile,
  SypMusicFileKey,
  SypMusicFileNew,
  visitAllMeasures,
} from '../../../common/src/syp_file.ts';
import { LocalStorageKey } from '../data/client_local_storage.ts';
import { useCurrentOrg } from '../data/use_current_org.tsx';
import { useCustomMusic } from '../data/use_custom_music.tsx';
import { HymnalsContext } from '../data/use_hymnals.tsx';
import { useLocalStorage } from '../data/use_local_storage.tsx';
import { useUserAttributes } from '../data/use_user_attributes.ts';
import { getCopyrightIssues } from '../util/copyright.ts';
import { NetworkError } from '../util/network.ts';
import { getHymnFromSlug, getSypUrlForHymn } from '../util/path.ts';
import { useRetryOnReconnect } from '../util/use_retry_on_reconnect.tsx';
import {
  isReloadDeferredByPlayback,
  isSongReloadHeld,
  noteSongReloadDeferred,
  shouldReloadForHashChange,
  SongFileVersion,
} from './song_reload.ts';
import { useSongReloadPulse } from './use_song_reload_pulse.ts';

export interface CurrentSong {
  hymn: Hymn | undefined;
  copyrightIssues: Copyright[] | undefined;
  sypFile: SypMusicFileNew | undefined;
  sypFileParsed: boolean;
  networkError: NetworkError | undefined;
  measureVisits: MeasureVisit[];
  measurePercentages: number[] | undefined;
  measurePercentagesWithRepeats: number[] | undefined;
  userKeyTransposition: number;
  setUserKeyTransposition: Dispatch<SetStateAction<number>>;
  tutorialPlayingSong: boolean;
  setTutorialPlayingSong: Dispatch<SetStateAction<boolean>>;
}

export const CurrentSongContext = createContext<CurrentSong>({} as any);

export const CurrentSongProvider = ({ children }: { children: ReactNode }) => {
  const [playlist] = useLocalStorage(LocalStorageKey.Playlist);
  const [playlistIndex] = useLocalStorage(LocalStorageKey.PlaylistIndex);
  const [sypFile, setSypFile] = useState<SypMusicFileNew | undefined>();
  const [sypFileParsed, setSypFileParsed] = useState(false);
  const [sypFilePath, setSypFilePath] = useState<string | undefined>();
  const [songsForWeek] = useLocalStorage(LocalStorageKey.WeeklySongList);
  const [orgHymnal] = useLocalStorage(LocalStorageKey.OrgHymnal);
  const { householdOrgs } = useCurrentOrg();
  const { isInternalUser } = useUserAttributes();
  const hymnals = use(HymnalsContext);
  const { customMusic } = useCustomMusic();
  const [networkError, setNetworkError] = useState<NetworkError | undefined>();
  const [userKeyTransposition, setUserKeyTransposition] = useState(0);
  const [tutorialPlayingSong, setTutorialPlayingSong] = useState(false);

  if (playlist && playlist.songs.length > 0 && playlistIndex >= playlist.songs.length) {
    console.error(
      `Playlist index (${playlistIndex}) expected to be less than playlist length (${playlist.songs.length})`,
    );
  }

  const hymn = useMemo(() => {
    const entry = playlist?.songs[playlistIndex];
    if (!isBound(entry)) {
      return;
    }
    return getHymnFromSlug({ songSlug: entry.slug, hymnals, customMusic });
  }, [customMusic, hymnals, playlist, playlistIndex]);

  // Reset userKeyTransposition whenever the current song changes - keyed on the slug, not the
  // hymn object, which a manifest refresh used to replace with an equal copy (issue #3517).
  // Uses the React-recommended "adjust state during render" pattern instead of a useEffect,
  // which avoids a wasted commit and satisfies @eslint-react/set-state-in-effect.
  const [prevHymnSlug, setPrevHymnSlug] = useState(hymn?.slug);
  if (prevHymnSlug !== hymn?.slug) {
    setPrevHymnSlug(hymn?.slug);
    setUserKeyTransposition(0);
    // a different song (or no song at all) starts with a clean error state
    setNetworkError(undefined);
  }

  // The notation fetch is keyed on what the fetch actually depends on - the song's url (slug,
  // hymnal, song number and base path) and its manifest content hash - rather than on the hymn
  // object, so a refresh of an unrelated hymnal no longer refetches the current song and resets
  // its error state (issue #3517 item A3).
  const sypUrl = hymn && getSypUrlForHymn(hymn);
  const hymnHash = hymn?.hash;
  // The manifest already says which files the song has, so a song whose .syp has not been
  // generated yet is known to be missing before any request is made (issue #3265). A primitive,
  // so the fetch effect below is not re-keyed on the hymn object.
  const isSypFileMissing = !!hymn && isSongFileMissing(hymn, SongFileExtension.Syp);

  // The song's notation is reloaded when its content hash changes (the cache refresher replaced
  // the file in place, so the url is unchanged) - but not while the song is playing, which would
  // swap the notation out from under the singers (issue #3517 item B1).
  const lastFetchedRef = useRef<SongFileVersion | undefined>(undefined);

  const fetchSypFile = useCallback(async (url: string, isFileMissing: boolean) => {
    if (isFileMissing) {
      // Nothing to ask for: report exactly what the 404 below would have reported, with no
      // request (issue #3265). lastFetchedRef is deliberately left pointing at this version, so
      // the hash change that brings the generated file is what triggers the fetch.
      setNetworkError(NetworkError.TextDownloadFailed);
      return;
    }
    setNetworkError(undefined);
    let response: Response;
    try {
      // fetch() REJECTS when the device is offline - outside the try this escaped unhandled and
      // left networkError unset, so nothing ever retried the song (issue #3517 item B6)
      response = await fetch(url);
    } catch (error: any) {
      console.log(`Error fetching syp file ${url}: ${error?.message ?? error}`);
      setNetworkError(NetworkError.TextDownloadFailed);
      // forget what we tried to load, so a later trigger fetches it again
      lastFetchedRef.current = undefined;
      return;
    }
    if (!response.ok) {
      // an expected transient, not an error - see onNetworkError in sequencer.tsx (issue #3265)
      console.log(`Error fetching syp file ${url}: ${response.statusText}`);
      setNetworkError(NetworkError.TextDownloadFailed);
      lastFetchedRef.current = undefined;
      return;
    }
    try {
      const content = await response.text();
      setSypFile(parseSypFile(content, { newFormat: true }));
      setSypFilePath(url);
    } catch (error: any) {
      console.error(`Error parsing syp file ${url}: ${error.message}`);
      setNetworkError(NetworkError.TextFileParseFailed);
      lastFetchedRef.current = undefined;
      return;
    }
  }, []);

  // A hash change while the song is playing is deferred, not dropped: the pulse advances when the
  // update gate opens, this effect re-runs and now gets 'same-song-refresh' (issue #3265).
  const songReloadPulse = useSongReloadPulse();

  useEffect(() => {
    const next: SongFileVersion | undefined = sypUrl ? { slug: sypUrl, hash: hymnHash } : undefined;
    const versions = { previous: lastFetchedRef.current, next, isHeld: isSongReloadHeld() };
    const action = shouldReloadForHashChange(versions);
    if (action === 'none') {
      if (isReloadDeferredByPlayback(versions)) {
        noteSongReloadDeferred();
      }
      return;
    }
    lastFetchedRef.current = next;
    if (!sypUrl) {
      return;
    }
    void fetchSypFile(sypUrl, isSypFileMissing);
  }, [fetchSypFile, sypUrl, hymnHash, isSypFileMissing, songReloadPulse]);

  // Retry a failed notation fetch when the device comes back online or the user returns to the
  // app, rather than leaving the song stuck on its error (issue #3517 item B6). A song with a
  // network error is never playing, and a hash change retries through the effect above.
  const retryFetch = useCallback(() => {
    if (!sypUrl) {
      return;
    }
    void fetchSypFile(sypUrl, isSypFileMissing);
  }, [fetchSypFile, sypUrl, isSypFileMissing]);
  // No point retrying a file the manifest says does not exist: reconnecting does not generate it,
  // and the hash change that does reloads the song anyway (issue #3265).
  useRetryOnReconnect(!!networkError && !!sypUrl && !isSypFileMissing, retryFetch);

  const resolvedHymn: Hymn | undefined = useMemo(() => {
    const isStaleSypFile = hymn && getSypUrlForHymn(hymn) !== sypFilePath;
    if (!hymn || !sypFile || isStaleSypFile || sypFilePath !== getSypUrlForHymn(hymn)) {
      return;
    }
    const { metadata } = sypFile;
    const maybeVerseCount = metadata[SypMusicFileKey.VerseCount];
    // in theory, this should be set above when the syp file is actually parsed, but we may be depending on
    // this not getting set based on the above condition

    setSypFileParsed(true);
    return {
      ...hymn,
      ...(maybeVerseCount && { verseCount: Number(maybeVerseCount) }),
      ...(metadata[SypMusicFileKey.Author] && { author: metadata[SypMusicFileKey.Author] }),
      ...(metadata[SypMusicFileKey.Composer] && { composer: metadata[SypMusicFileKey.Composer] }),
      ...(metadata[SypMusicFileKey.Harmony] && { harmony: metadata[SypMusicFileKey.Harmony] }),
      ...(metadata[SypMusicFileKey.Copyright] && { copyright: metadata[SypMusicFileKey.Copyright] }),
      ...(metadata[SypMusicFileKey.Key] && { key: metadata[SypMusicFileKey.Key] }),
      ...(metadata[SypMusicFileKey.Vocals] && { vocals: metadata[SypMusicFileKey.Vocals] }),
      ...(metadata[SypMusicFileKey.VocalPartCount] && {
        vocalPartCount: Number(metadata[SypMusicFileKey.VocalPartCount]),
      }),
    };
  }, [hymn, sypFile, sypFilePath]);

  // Overriding hymn.issue would be more ideal, but that creates a UI glitch when the song first loads
  // because it initially contains an issue and then that issue goes away once we get here.
  // In theory, we might be able to move the checks below upstream, perhaps even into the hymnal contexts.
  const copyrightIssues: Copyright[] | undefined = useMemo(
    () => getCopyrightIssues({ hymn: resolvedHymn, isInternalUser, songsForWeek, householdOrgs, hymnals, orgHymnal }),
    [householdOrgs, hymnals, isInternalUser, orgHymnal, resolvedHymn, songsForWeek],
  );

  const measureVisits: MeasureVisit[] = useMemo(() => {
    if (!sypFile) {
      return [];
    }
    try {
      return visitAllMeasures(sypFile.measures);
    } catch (e: any) {
      console.error(`Error visiting measures: ${e.message}`);
      return [];
    }
  }, [sypFile]);

  // percentage location of each measure in the slider (no repeats)
  const measurePercentages = useMemo(() => {
    if (!hymn || !sypFile) {
      return;
    }
    return getMeasurePercentages(sypFile.measures);
  }, [hymn, sypFile]);

  const measurePercentagesWithRepeats = useMemo(() => {
    if (!hymn || !measureVisits) {
      return;
    }
    return getMeasurePercentages(measureVisits.map(({ measure }) => measure));
  }, [hymn, measureVisits]);

  return (
    <CurrentSongContext
      value={{
        hymn: resolvedHymn ?? hymn,
        copyrightIssues,
        sypFile,
        sypFileParsed: sypFileParsed,
        networkError,
        measureVisits,
        measurePercentages,
        measurePercentagesWithRepeats,
        userKeyTransposition,
        setUserKeyTransposition,
        tutorialPlayingSong,
        setTutorialPlayingSong,
      }}
    >
      {children}
    </CurrentSongContext>
  );
};

export { getMeasureFromPercent, roundMeasurePercent } from '../../../common/src/measure_percentages.ts';
